import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Spinner, Alert, Form, Row, Col, Table, Badge } from 'react-bootstrap';
import { FiCalendar, FiArrowLeft, FiPlus, FiFileText, FiUsers, FiCheckCircle, FiRefreshCw } from 'react-icons/fi';
import { createActivityRequest, createActivityQuoteRequest, createPolicyRequest } from '../../store/actions/dataActions';
import { getAllPartners, getActivitySubcoveragesByPartner, getClientsByPartner } from '../../services/api';
import './AdminActivities.css';

// Fonction pour convertir une date du format "DD/MM/YYYY" en "YYYY-MM-DD" pour les inputs date
const convertDateToInputFormat = (dateStr) => {
    if (!dateStr || dateStr === '') return '';
    
    try {
        // Format attendu: "01/01/2025" (DD/MM/YYYY)
        const parts = dateStr.split('/');
        if (parts.length === 3) {
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            const year = parts[2];
            return `${year}-${month}-${day}`;
        }
    } catch (error) {
        console.error('Erreur lors de la conversion de la date:', error);
    }
    
    return '';
};

const AdminActivityCreate = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { createActivity, createActivityQuote, createPolicy } = useSelector((state) => state.data);
    const { isAuthenticated } = useSelector((state) => state.adminAuth);

    const [selectedPartnerId, setSelectedPartnerId] = useState('');
    const [selectedSubcoverageId, setSelectedSubcoverageId] = useState('');
    const [partners, setPartners] = useState([]);
    const [subcoverages, setSubcoverages] = useState([]);
    const [loadingSubcoverages, setLoadingSubcoverages] = useState(false);
    const [formData, setFormData] = useState({
        category: '',
        name: '',
        activity_start_date: '',
        activity_start_time: '00:00',
        activity_end_date: '',
        activity_end_time: '00:00',
        activity_price: '',
        reservation_number: '',
        combined_addres: '',
    });
    const [errors, setErrors] = useState({});
    
    // États pour le flux multi-étapes
    const [currentStep, setCurrentStep] = useState(1); // 1: Activity, 2: Quote, 3: Clients, 4: Policies
    const [createdActivityId, setCreatedActivityId] = useState(null);
    const [createdQuoteId, setCreatedQuoteId] = useState(null);
    const [clients, setClients] = useState([]);
    const [selectedClients, setSelectedClients] = useState([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [policyFormData, setPolicyFormData] = useState({
        preferred_language: 'fr-FR',
        handling_mode: 'All',
        uw_date: '',
    });
    const [creatingPolicies, setCreatingPolicies] = useState(false);
    const [policiesCreated, setPoliciesCreated] = useState(0);
    const [policiesErrors, setPoliciesErrors] = useState({});

    // Charger les partenaires
    useEffect(() => {
        const loadPartners = async () => {
            try {
                const response = await getAllPartners();
                let partnersList = [];
                if (response?.users && Array.isArray(response.users)) {
                    partnersList = response.users;
                } else if (Array.isArray(response)) {
                    partnersList = response;
                } else if (response?.partners && Array.isArray(response.partners)) {
                    partnersList = response.partners;
                } else if (response?.data && Array.isArray(response.data)) {
                    partnersList = response.data;
                }
                setPartners(partnersList);
            } catch (error) {
                console.error('Erreur lors du chargement des partenaires:', error);
            }
        };
        if (isAuthenticated) {
            loadPartners();
        }
    }, [isAuthenticated]);

    // Charger les sous-couvertures du partenaire sélectionné
    useEffect(() => {
        const loadSubcoverages = async () => {
            if (!selectedPartnerId) {
                setSubcoverages([]);
                setSelectedSubcoverageId('');
                return;
            }

            setLoadingSubcoverages(true);
            try {
                const response = await getActivitySubcoveragesByPartner(selectedPartnerId);
                const subcoveragesList = response?.subcoverages || [];
                setSubcoverages(subcoveragesList);
                if (selectedSubcoverageId && !subcoveragesList.find(item => (item.subcoverage?.sub_coverage_id || item.subcoverage?.id) === selectedSubcoverageId)) {
                    setSelectedSubcoverageId('');
                }
            } catch (error) {
                console.error('Erreur lors du chargement des sous-couvertures:', error);
                setSubcoverages([]);
                setSelectedSubcoverageId('');
            } finally {
                setLoadingSubcoverages(false);
            }
        };

        loadSubcoverages();
    }, [selectedPartnerId, selectedSubcoverageId]);

    // Pré-remplir les champs depuis la sous-couverture sélectionnée
    useEffect(() => {
        if (selectedSubcoverageId && subcoverages.length > 0) {
            const selectedSubcoverage = subcoverages.find(item => 
                (item.subcoverage?.sub_coverage_id || item.subcoverage?.id) === selectedSubcoverageId
            );
            
            if (selectedSubcoverage) {
                const subcoverage = selectedSubcoverage.subcoverage || {};
                const reference = selectedSubcoverage.reference || {};
                
                // Construire l'adresse combinée depuis la sous-couverture
                const combinedAddress = [
                    subcoverage.activity_address || '',
                    subcoverage.zip_code || '',
                    subcoverage.city || '',
                    subcoverage.country || ''
                ].filter(Boolean).join(',');
                
                // Convertir les dates du format "DD/MM/YYYY" en "YYYY-MM-DD"
                const startDate = convertDateToInputFormat(subcoverage.sub_coverage_start_date || '');
                const endDate = convertDateToInputFormat(subcoverage.sub_coverage_end_date || '');
                
                setFormData(prev => ({
                    ...prev,
                    category: reference.category || '',
                    name: reference.name || '',
                    combined_addres: combinedAddress,
                    activity_start_date: startDate,
                    activity_end_date: endDate,
                }));
            }
        }
    }, [selectedSubcoverageId, subcoverages]);

    // Après création de l'activité, créer la quote automatiquement
    useEffect(() => {
        if (createActivity.success && createActivity.data) {
            console.log('✅ Activity created successfully, data:', createActivity.data);
            // La saga envoie { activity: createdActivity } dans le payload
            const activityData = createActivity.data.activity || createActivity.data;
            const activityId = activityData?.activity_id || activityData?.id;
            
            console.log('📋 Extracted activityId:', activityId);
            console.log('📋 Activity data:', activityData);
            
            if (activityId) {
                setCreatedActivityId(activityId);
                setCurrentStep(2); // Passer à l'étape de création de quote
                console.log('🚀 Creating quote for activity:', activityId);
                dispatch(createActivityQuoteRequest(activityId));
            } else {
                console.error('❌ Activity ID not found in response:', createActivity);
            }
        }
    }, [createActivity.success, createActivity.data, dispatch]);

    // Après création de la quote, charger les clients et passer à l'étape de sélection
    useEffect(() => {
        console.log('🔍 Checking quote creation:', {
            success: createActivityQuote.success,
            hasData: !!createActivityQuote.data,
            data: createActivityQuote.data,
            loading: createActivityQuote.loading,
            error: createActivityQuote.error
        });
        
        if (createActivityQuote.success && createActivityQuote.data) {
            console.log('✅ Quote created successfully, full data:', createActivityQuote.data);
            // La réponse est { quote: { quote_id: "...", ... } }
            const quote = createActivityQuote.data.quote || createActivityQuote.data.quotes || createActivityQuote.data;
            // Essayer plusieurs chemins pour trouver le quote_id
            const quoteId = quote?.quote_id || quote?.id || 
                           createActivityQuote.data?.quote?.quote_id ||
                           createActivityQuote.data?.quote_id || 
                           createActivityQuote.data?.id ||
                           (typeof createActivityQuote.data === 'string' ? createActivityQuote.data : null);
            
            console.log('📋 Extracted quoteId:', quoteId);
            console.log('📋 Quote object:', quote);
            console.log('📋 Full createActivityQuote.data:', createActivityQuote.data);
            
            if (quoteId) {
                setCreatedQuoteId(quoteId);
                setCurrentStep(3); // Passer à l'étape de sélection des clients
                console.log('🚀 Moving to step 3, loading clients for partner:', selectedPartnerId);
                
                // Charger les clients du partenaire
                if (selectedPartnerId) {
                    loadClients();
                } else {
                    console.error('❌ Partner ID not found, cannot load clients');
                }
            } else {
                console.error('❌ Quote ID not found in response. Full data:', createActivityQuote.data);
            }
        } else if (createActivityQuote.success && !createActivityQuote.data) {
            console.warn('⚠️ Quote creation succeeded but no data in response');
        } else if (createActivityQuote.error) {
            console.error('❌ Quote creation failed:', createActivityQuote.error);
        }
    }, [createActivityQuote.success, createActivityQuote.data, createActivityQuote.error, selectedPartnerId]);

    // Ref pour suivre les créations de policies
    const policiesCreatedRef = useRef(0);
    const previousPolicySuccessRef = useRef(false);
    
    // Suivre les créations de policies
    useEffect(() => {
        // Détecter quand createPolicy.success passe de false à true (nouvelle création)
        const currentSuccess = createPolicy.success;
        const previousSuccess = previousPolicySuccessRef.current;
        
        if (currentSuccess && !previousSuccess && creatingPolicies) {
            policiesCreatedRef.current += 1;
            setPoliciesCreated(policiesCreatedRef.current);
            
            // Réinitialiser le success après un court délai pour permettre de détecter la prochaine création
            setTimeout(() => {
                previousPolicySuccessRef.current = false;
            }, 100);
            
            if (policiesCreatedRef.current >= selectedClients.length) {
                setCreatingPolicies(false);
                setTimeout(() => {
                    navigate('/activity/activities');
                }, 2000);
            }
        }
        
        previousPolicySuccessRef.current = currentSuccess;
    }, [createPolicy.success, creatingPolicies, selectedClients.length, navigate]);

    // Charger les clients du partenaire
    const loadClients = async () => {
        if (!selectedPartnerId) {
            console.error('❌ Cannot load clients: partner ID is missing');
            return;
        }
        
        console.log('📥 Loading clients for partner:', selectedPartnerId);
        setLoadingClients(true);
        try {
            const response = await getClientsByPartner(selectedPartnerId);
            console.log('✅ Clients loaded, response:', response);
            // La réponse de l'API est { "clients": [...] }
            const clientsList = response?.clients || response?.data || response || [];
            console.log('📋 Clients list:', clientsList);
            setClients(Array.isArray(clientsList) ? clientsList : []);
        } catch (error) {
            console.error('❌ Erreur lors du chargement des clients:', error);
            setClients([]);
        } finally {
            setLoadingClients(false);
        }
    };

    // Gérer la sélection des clients
    const handleClientToggle = (clientId) => {
        setSelectedClients(prev => {
            if (prev.includes(clientId)) {
                return prev.filter(id => id !== clientId);
            } else {
                return [...prev, clientId];
            }
        });
    };

    // Gérer le changement des données de policy
    const handlePolicyFormChange = (e) => {
        const { name, value } = e.target;
        setPolicyFormData(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    // Valider le formulaire de policy
    const validatePolicyForm = () => {
        const newErrors = {};
        
        if (!policyFormData.uw_date) {
            newErrors.uw_date = 'La date UW est requise';
        } else {
            // Valider le format de date DD/MM/YYYY
            const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
            if (!dateRegex.test(policyFormData.uw_date)) {
                newErrors.uw_date = 'Le format de date doit être DD/MM/YYYY';
            }
        }

        if (!policyFormData.preferred_language) {
            newErrors.preferred_language = 'La langue préférée est requise';
        }

        setPoliciesErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Créer les policies pour tous les clients sélectionnés
    const handleCreatePolicies = async () => {
        if (!validatePolicyForm()) {
            console.error('❌ Policy form validation failed');
            return;
        }

        if (selectedClients.length === 0) {
            alert('Veuillez sélectionner au moins un client');
            return;
        }

        if (!createdQuoteId) {
            console.error('❌ Quote ID is missing');
            alert('Erreur: Quote ID manquant');
            return;
        }

        console.log('🚀 Creating policies for', selectedClients.length, 'clients');
        console.log('📋 Quote ID:', createdQuoteId);
        console.log('📋 Selected clients:', selectedClients);
        console.log('📋 Policy data:', policyFormData);

        setCreatingPolicies(true);
        setPoliciesCreated(0);
        policiesCreatedRef.current = 0;
        previousPolicySuccessRef.current = false;

        // Créer les policies une par une avec un délai entre chaque
        for (let i = 0; i < selectedClients.length; i++) {
            const clientId = selectedClients[i];
            console.log(`📝 Creating policy ${i + 1}/${selectedClients.length} for client:`, clientId);
            dispatch(createPolicyRequest(createdQuoteId, clientId, {
                preferred_language: policyFormData.preferred_language,
                handling_mode: policyFormData.handling_mode || null,
                uw_date: policyFormData.uw_date,
            }));
            
            // Attendre un peu entre chaque création pour éviter de surcharger l'API
            // et laisser le temps à la saga de traiter chaque requête
            if (i < selectedClients.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;
        
        // Pour les champs d'heure, garder le format HH:MM (sans secondes dans l'input)
        if ((name === 'activity_start_time' || name === 'activity_end_time') && value) {
            // Si l'heure est au format HH:MM:SS, extraire seulement HH:MM
            if (value.length === 8 && value.match(/^\d{2}:\d{2}:\d{2}$/)) {
                processedValue = value.substring(0, 5); // Garder seulement HH:MM
            }
            // Si l'heure est au format HH:MM, garder tel quel
            else if (value.length === 5 && value.match(/^\d{2}:\d{2}$/)) {
                processedValue = value;
            }
            // Si l'heure n'a pas le bon format, essayer de la corriger
            else if (value.length > 0) {
                const timeMatch = value.match(/^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/);
                if (timeMatch) {
                    const hours = timeMatch[1].padStart(2, '0');
                    const minutes = timeMatch[2].padStart(2, '0');
                    processedValue = `${hours}:${minutes}`; // Format HH:MM seulement
                }
            }
        }
        
        setFormData(prev => ({
            ...prev,
            [name]: processedValue,
        }));
        // Effacer l'erreur pour ce champ
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null,
            }));
        }
        // Si on vide un champ, le mettre à null pour l'API
        if (processedValue === '') {
            setFormData(prev => ({
                ...prev,
                [name]: null,
            }));
        }
        // Si on modifie une date ou une heure, effacer aussi les erreurs liées aux dates/heures
        if (name === 'activity_start_date' || name === 'activity_start_time' || 
            name === 'activity_end_date' || name === 'activity_end_time') {
            setErrors(prev => ({
                ...prev,
                activity_start_date: null,
                activity_end_date: null,
                activity_start_time: null,
                activity_end_time: null,
            }));
        }
    };

    // Fonction pour générer un numéro de réservation automatique
    const generateReservationNumber = () => {
        // Générer exactement 12 chiffres aléatoires
        let randomDigits = '';
        for (let i = 0; i < 12; i++) {
            randomDigits += Math.floor(Math.random() * 10).toString();
        }
        const reservationNumber = `RSV${randomDigits}`;
        setFormData(prev => ({
            ...prev,
            reservation_number: reservationNumber
        }));
    };

    const handlePartnerChange = (e) => {
        setSelectedPartnerId(e.target.value);
        setSelectedSubcoverageId('');
        setFormData(prev => ({
            ...prev,
            category: '',
            name: '',
            combined_addres: '',
            activity_start_date: '',
            activity_end_date: '',
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!selectedPartnerId) {
            newErrors.partner = 'Veuillez sélectionner un partenaire';
        }

        if (!selectedSubcoverageId) {
            newErrors.subcoverage = 'Veuillez sélectionner une sous-couverture';
        }

        if (!formData.category) {
            newErrors.category = 'La catégorie est requise';
        }

        if (!formData.name) {
            newErrors.name = 'Le nom est requis';
        }

        if (!formData.activity_start_date) {
            newErrors.activity_start_date = 'La date de début est requise';
        }

        if (!formData.activity_end_date) {
            newErrors.activity_end_date = 'La date de fin est requise';
        }

        if (formData.activity_start_date && formData.activity_end_date) {
            const startDate = new Date(formData.activity_start_date);
            const endDate = new Date(formData.activity_end_date);
            
            // Comparer les dates sans l'heure (juste la date)
            const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
            const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
            
            if (endDateOnly < startDateOnly) {
                // Si la date de fin est antérieure à la date de début, c'est invalide
                newErrors.activity_end_date = 'La date de fin doit être postérieure ou égale à la date de début';
            } else if (endDateOnly.getTime() === startDateOnly.getTime()) {
                // Si les dates sont identiques, vérifier les heures
                // Les heures sont au format HH:MM (sans secondes)
                let startTime = formData.activity_start_time || '00:00';
                let endTime = formData.activity_end_time || '00:00';
                
                // Extraire les heures et minutes
                const [startHours, startMinutes] = startTime.split(':').map(Number).slice(0, 2);
                const [endHours, endMinutes] = endTime.split(':').map(Number).slice(0, 2);
                
                const startTotalMinutes = startHours * 60 + startMinutes;
                const endTotalMinutes = endHours * 60 + endMinutes;
                
                if (endTotalMinutes <= startTotalMinutes) {
                    newErrors.activity_end_time = 'L\'heure de fin doit être supérieure à l\'heure de début lorsque les dates sont identiques';
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Convertir les dates au format "DEC 23, 2025 4:00 pm"
        const formatDateTime = (date, time) => {
            if (!date) return null;
            const dateObj = new Date(date);
            const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
            const month = months[dateObj.getMonth()];
            const day = dateObj.getDate();
            const year = dateObj.getFullYear();
            
            let hour = 12;
            let minute = 0;
            let ampm = 'pm';
            
            if (time) {
                // S'assurer que l'heure a le format HH:MM:SS avec ":00" pour les secondes par défaut
                let formattedTime = time;
                if (formattedTime.length === 5 && formattedTime.match(/^\d{2}:\d{2}$/)) {
                    formattedTime = `${formattedTime}:00`;
                }
                // Gérer le format HH:MM ou HH:MM:SS
                const timeParts = formattedTime.split(':');
                hour = parseInt(timeParts[0], 10);
                minute = parseInt(timeParts[1] || 0, 10);
                // Les secondes (timeParts[2]) sont ignorées pour l'affichage mais présentes dans le format
                ampm = hour >= 12 ? 'pm' : 'am';
                if (hour > 12) hour -= 12;
                if (hour === 0) hour = 12;
            }
            
            return `${month} ${day}, ${year} ${hour}:${minute.toString().padStart(2, '0')} ${ampm}`;
        };

        const activityStartDate = formatDateTime(formData.activity_start_date, formData.activity_start_time);
        const activityEndDate = formatDateTime(formData.activity_end_date, formData.activity_end_time);

        const dataToSend = {
            category: formData.category,
            name: formData.name,
            activity_start_date: activityStartDate,
            activity_end_date: activityEndDate,
            activity_price: formData.activity_price ? parseFloat(formData.activity_price) : null,
            reservation_number: formData.reservation_number || null,
            combined_addres: formData.combined_addres || null,
        };

        dispatch(createActivityRequest(selectedPartnerId, dataToSend));
    };

    return (
        <div className="dashboard-page admin-activities-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiCalendar className="me-2" />
                                Créer une activité
                            </h2>
                            <p className="text-muted mb-0">Remplissez le formulaire pour créer une nouvelle activité</p>
                        </div>
                        <Button variant="outline-secondary" onClick={() => navigate('/activity/activities')}>
                            <FiArrowLeft className="me-2" />
                            Retour
                        </Button>
                    </div>

                    {/* Indicateur d'étapes */}
                    {currentStep > 1 && (
                        <Card className="mt-3 mb-3">
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center gap-3">
                                        <Badge bg={currentStep >= 1 ? 'success' : 'secondary'} className="p-2">
                                            <FiCalendar className="me-1" /> 1. Activité
                                        </Badge>
                                        <Badge bg={currentStep >= 2 ? 'success' : currentStep === 2 ? 'primary' : 'secondary'} className="p-2">
                                            <FiFileText className="me-1" /> 2. Quote
                                        </Badge>
                                        <Badge bg={currentStep >= 3 ? 'success' : currentStep === 3 ? 'primary' : 'secondary'} className="p-2">
                                            <FiUsers className="me-1" /> 3. Clients
                                        </Badge>
                                        <Badge bg={currentStep >= 4 ? 'success' : currentStep === 4 ? 'primary' : 'secondary'} className="p-2">
                                            <FiCheckCircle className="me-1" /> 4. Policies
                                        </Badge>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {createActivity.error && (
                        <Alert variant="danger" className="mt-3">
                            {createActivity.error}
                        </Alert>
                    )}
                    {createActivityQuote.error && (
                        <Alert variant="danger" className="mt-3">
                            {createActivityQuote.error}
                        </Alert>
                    )}
                    {createPolicy.error && (
                        <Alert variant="danger" className="mt-3">
                            {createPolicy.error}
                        </Alert>
                    )}

                    {/* Étape 1: Création de l'activité */}
                    {currentStep === 1 && (
                        <Card className="mt-4">
                            <Card.Body>
                                <Form onSubmit={handleSubmit}>
                                <Row className="mb-3">
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>Partenaire *</Form.Label>
                                            <Form.Select
                                                value={selectedPartnerId}
                                                onChange={handlePartnerChange}
                                                required
                                                disabled={createActivity.loading}
                                                isInvalid={!!errors.partner}
                                            >
                                                <option value="">Sélectionner un partenaire...</option>
                                                {partners.map((partner) => {
                                                    const partnerId = partner.user_uuid || partner.partner_id || partner.id;
                                                    const partnerName = partner.company_name || `${partner.partner_first_name || partner.first_name || ''} ${partner.partner_last_name || partner.last_name || ''}`.trim() || partner.email || 'N/A';
                                                    return (
                                                        <option key={partnerId} value={partnerId}>
                                                            {partnerName}
                                                        </option>
                                                    );
                                                })}
                                            </Form.Select>
                                            {errors.partner && (
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.partner}
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>Sous-couverture *</Form.Label>
                                            {loadingSubcoverages ? (
                                                <div className="d-flex align-items-center">
                                                    <Spinner size="sm" className="me-2" />
                                                    <span className="text-muted">Chargement des sous-couvertures...</span>
                                                </div>
                                            ) : (
                                                <Form.Select
                                                    value={selectedSubcoverageId}
                                                    onChange={(e) => setSelectedSubcoverageId(e.target.value)}
                                                    required
                                                    disabled={createActivity.loading || !selectedPartnerId || subcoverages.length === 0}
                                                    isInvalid={!!errors.subcoverage}
                                                >
                                                    <option value="">
                                                        {!selectedPartnerId 
                                                            ? 'Sélectionnez d\'abord un partenaire...' 
                                                            : subcoverages.length === 0 
                                                                ? 'Aucune sous-couverture disponible pour ce partenaire' 
                                                                : 'Sélectionner une sous-couverture...'}
                                                    </option>
                                                    {subcoverages.map((item) => {
                                                        const subcoverage = item.subcoverage || {};
                                                        const reference = item.reference || {};
                                                        const subcoverageId = subcoverage.sub_coverage_id || subcoverage.id;
                                                        const startDate = subcoverage.sub_coverage_start_date || '';
                                                        const endDate = subcoverage.sub_coverage_end_date || '';
                                                        const period = startDate && endDate ? `${startDate} - ${endDate}` : 'Période non définie';
                                                        const displayName = `${reference.name || 'Sans nom'} - ${period} (${reference.category || 'N/A'})`;
                                                        return (
                                                            <option key={subcoverageId} value={subcoverageId}>
                                                                {displayName}
                                                            </option>
                                                        );
                                                    })}
                                                </Form.Select>
                                            )}
                                            {errors.subcoverage && (
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.subcoverage}
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Catégorie *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="category"
                                                value={formData.category}
                                                onChange={handleChange}
                                                required
                                                disabled={createActivity.loading}
                                                isInvalid={!!errors.category}
                                            />
                                            {errors.category && (
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.category}
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>Nom *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                disabled={createActivity.loading}
                                                isInvalid={!!errors.name}
                                            />
                                            {errors.name && (
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.name}
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Date de début *</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="activity_start_date"
                                                value={formData.activity_start_date}
                                                onChange={handleChange}
                                                required
                                                disabled={createActivity.loading}
                                                isInvalid={!!errors.activity_start_date}
                                            />
                                            {errors.activity_start_date && (
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.activity_start_date}
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Heure de début</Form.Label>
                                            <Form.Control
                                                type="time"
                                                name="activity_start_time"
                                                value={formData.activity_start_time}
                                                onChange={handleChange}
                                                disabled={createActivity.loading}
                                                step="60"
                                            />
                                            <Form.Text className="text-muted">
                                                Format: HH:MM (les secondes sont automatiquement définies à 00)
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Date de fin *</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="activity_end_date"
                                                value={formData.activity_end_date}
                                                onChange={handleChange}
                                                required
                                                disabled={createActivity.loading}
                                                isInvalid={!!errors.activity_end_date}
                                            />
                                            {errors.activity_end_date && (
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.activity_end_date}
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Heure de fin</Form.Label>
                                            <Form.Control
                                                type="time"
                                                name="activity_end_time"
                                                value={formData.activity_end_time}
                                                onChange={handleChange}
                                                disabled={createActivity.loading}
                                                step="60"
                                                isInvalid={!!errors.activity_end_time}
                                            />
                                            <Form.Text className="text-muted">
                                                Format: HH:MM (les secondes sont automatiquement définies à 00)
                                            </Form.Text>
                                            {errors.activity_end_time && (
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.activity_end_time}
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Prix de l'activité</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="activity_price"
                                                value={formData.activity_price}
                                                onChange={handleChange}
                                                disabled={createActivity.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Numéro de réservation</Form.Label>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <Form.Control
                                                    type="text"
                                                    name="reservation_number"
                                                    value={formData.reservation_number}
                                                    onChange={handleChange}
                                                    disabled={createActivity.loading}
                                                    placeholder="RSV123456789012"
                                                />
                                                <Button
                                                    variant="outline-secondary"
                                                    onClick={generateReservationNumber}
                                                    disabled={createActivity.loading}
                                                    title="Générer un numéro de réservation automatique"
                                                    style={{ whiteSpace: 'nowrap' }}
                                                >
                                                    <FiRefreshCw className="me-1" />
                                                    Générer
                                                </Button>
                                            </div>
                                            <Form.Text className="text-muted">
                                                Format: RSV suivi de 12 chiffres (ex: RSV123456789012)
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>Adresse combinée</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="combined_addres"
                                                value={formData.combined_addres}
                                                onChange={handleChange}
                                                placeholder="Ex: 1 port du louvre,75001,paris,france"
                                                disabled={createActivity.loading}
                                            />
                                            <Form.Text className="text-muted">
                                                Format: adresse,code postal,ville,pays
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <div className="d-flex justify-content-end gap-2 mt-4">
                                    <Button
                                        variant="secondary"
                                        onClick={() => navigate('/activity/activities')}
                                        disabled={createActivity.loading}
                                    >
                                        Annuler
                                    </Button>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={createActivity.loading || !selectedPartnerId || !selectedSubcoverageId || loadingSubcoverages}
                                    >
                                        {createActivity.loading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Création...
                                            </>
                                        ) : (
                                            <>
                                                <FiPlus className="me-2" />
                                                Créer l'activité
                                            </>
                                        )}
                                    </Button>
                                </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Étape 2: Création de la quote */}
                    {currentStep === 2 && (
                        <Card className="mt-4">
                            <Card.Body>
                                {createActivityQuote.loading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-3">Création de la quote en cours...</p>
                                    </div>
                                ) : createActivityQuote.error ? (
                                    <Alert variant="danger">
                                        <Alert.Heading>Erreur lors de la création de la quote</Alert.Heading>
                                        <p>{createActivityQuote.error}</p>
                                        <Button variant="primary" onClick={() => navigate('/activity/activities')}>
                                            Retour à la liste
                                        </Button>
                                    </Alert>
                                ) : (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-3">Quote créée avec succès. Chargement des clients...</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Étape 3: Sélection des clients */}
                    {currentStep === 3 && (
                        <Card className="mt-4">
                            <Card.Header>
                                <h5 className="mb-0">
                                    <FiUsers className="me-2" />
                                    Sélectionner les clients
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                {loadingClients ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" variant="primary" />
                                        <p className="mt-3">Chargement des clients...</p>
                                    </div>
                                ) : clients.length === 0 ? (
                                    <Alert variant="warning">
                                        <Alert.Heading>Aucun client trouvé</Alert.Heading>
                                        <p>Aucun client n'est associé à ce partenaire.</p>
                                        <Button variant="primary" onClick={() => navigate('/activity/activities')}>
                                            Retour à la liste
                                        </Button>
                                    </Alert>
                                ) : (
                                    <>
                                        <Alert variant="info" className="mb-3">
                                            Sélectionnez les clients pour lesquels vous souhaitez créer des policies. 
                                            {selectedClients.length > 0 && (
                                                <span className="ms-2 fw-bold">{selectedClients.length} client(s) sélectionné(s)</span>
                                            )}
                                        </Alert>
                                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                            <Table striped hover>
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: '50px' }}>Sélection</th>
                                                        <th>Nom</th>
                                                        <th>Email</th>
                                                        <th>Code</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {clients.map((client) => {
                                                        const clientId = client.client_id || client.id;
                                                        const isSelected = selectedClients.includes(clientId);
                                                        return (
                                                            <tr key={clientId} style={{ cursor: 'pointer' }} onClick={() => handleClientToggle(clientId)}>
                                                                <td>
                                                                    <Form.Check
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        onChange={() => handleClientToggle(clientId)}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    {client.first_name || ''} {client.last_name || ''}
                                                                </td>
                                                                <td>{client.email || 'N/A'}</td>
                                                                <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                                    {client.client_code || client.code || 'N/A'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </div>
                                        <div className="d-flex justify-content-end gap-2 mt-4">
                                            <Button
                                                variant="secondary"
                                                onClick={() => navigate('/activity/activities')}
                                            >
                                                Annuler
                                            </Button>
                                            <Button
                                                variant="primary"
                                                onClick={() => setCurrentStep(4)}
                                                disabled={selectedClients.length === 0}
                                            >
                                                Continuer ({selectedClients.length} client(s) sélectionné(s))
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    )}

                    {/* Étape 4: Création des policies */}
                    {currentStep === 4 && (
                        <Card className="mt-4">
                            <Card.Header>
                                <h5 className="mb-0">
                                    <FiCheckCircle className="me-2" />
                                    Créer les policies
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Alert variant="info" className="mb-3">
                                    Vous allez créer {selectedClients.length} policy(s) pour les clients sélectionnés.
                                </Alert>
                                
                                <Form>
                                    <Row className="mb-3">
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>Langue préférée *</Form.Label>
                                                <Form.Select
                                                    name="preferred_language"
                                                    value={policyFormData.preferred_language}
                                                    onChange={handlePolicyFormChange}
                                                    required
                                                    disabled={creatingPolicies}
                                                    isInvalid={!!policiesErrors.preferred_language}
                                                >
                                                    <option value="fr-FR">Français (fr-FR)</option>
                                                    <option value="en-US">Anglais US (en-US)</option>
                                                    <option value="en_GB">Anglais GB (en_GB)</option>
                                                    <option value="de-DE">Allemand (de-DE)</option>
                                                    <option value="de_DE">Allemand (de_DE)</option>
                                                    <option value="es-ES">Espagnol (es-ES)</option>
                                                    <option value="es_ES">Espagnol (es_ES)</option>
                                                    <option value="nl-NL">Néerlandais (nl-NL)</option>
                                                    <option value="nl_NL">Néerlandais (nl_NL)</option>
                                                    <option value="da-DA">Danois (da-DA)</option>
                                                    <option value="da_DA">Danois (da_DA)</option>
                                                    <option value="it-IT">Italien (it-IT)</option>
                                                    <option value="it_IT">Italien (it_IT)</option>
                                                </Form.Select>
                                                {policiesErrors.preferred_language && (
                                                    <Form.Control.Feedback type="invalid">
                                                        {policiesErrors.preferred_language}
                                                    </Form.Control.Feedback>
                                                )}
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>Mode de gestion</Form.Label>
                                                <Form.Select
                                                    name="handling_mode"
                                                    value={policyFormData.handling_mode}
                                                    onChange={handlePolicyFormChange}
                                                    disabled={creatingPolicies}
                                                >
                                                    <option value="">Aucun</option>
                                                    <option value="Uw">Uw</option>
                                                    <option value="Confirmation_uw">Confirmation_uw</option>
                                                    <option value="Claims">Claims</option>
                                                    <option value="Uw + Confirmation_uw">Uw + Confirmation_uw</option>
                                                    <option value="Confirmation_uw + Claims">Confirmation_uw + Claims</option>
                                                    <option value="All">All</option>
                                                    <option value="Manual">Manual</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>Date UW * (DD/MM/YYYY)</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="uw_date"
                                                    value={policyFormData.uw_date}
                                                    onChange={handlePolicyFormChange}
                                                    placeholder="09/06/2025"
                                                    required
                                                    disabled={creatingPolicies}
                                                    isInvalid={!!policiesErrors.uw_date}
                                                />
                                                <Form.Text className="text-muted">
                                                    Format: DD/MM/YYYY (ex: 09/06/2025)
                                                </Form.Text>
                                                {policiesErrors.uw_date && (
                                                    <Form.Control.Feedback type="invalid">
                                                        {policiesErrors.uw_date}
                                                    </Form.Control.Feedback>
                                                )}
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Form>

                                {creatingPolicies && (
                                    <Alert variant="info" className="mt-3">
                                        <Spinner size="sm" className="me-2" />
                                        Création des policies en cours... ({policiesCreated}/{selectedClients.length})
                                    </Alert>
                                )}

                                {policiesCreated === selectedClients.length && policiesCreated > 0 && !creatingPolicies && (
                                    <Alert variant="success" className="mt-3">
                                        <Alert.Heading>Succès !</Alert.Heading>
                                        <p>Toutes les policies ont été créées avec succès ({policiesCreated} policy(s)).</p>
                                        <p>Redirection vers la liste des activités...</p>
                                    </Alert>
                                )}

                                <div className="d-flex justify-content-end gap-2 mt-4">
                                    <Button
                                        variant="secondary"
                                        onClick={() => setCurrentStep(3)}
                                        disabled={creatingPolicies}
                                    >
                                        Retour
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={handleCreatePolicies}
                                        disabled={creatingPolicies || !policyFormData.uw_date}
                                    >
                                        {creatingPolicies ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Création... ({policiesCreated}/{selectedClients.length})
                                            </>
                                        ) : (
                                            <>
                                                <FiCheckCircle className="me-2" />
                                                Créer les policies ({selectedClients.length})
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminActivityCreate;
